import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Grid, IconButton, Stack, TextField, Typography, LinearProgress } from '@mui/material';
import {
  PlayArrow,
  Pause,
  SkipNext,
  CheckCircle,
  CheckCircleOutline,
  DirectionsRun,
  EmojiEventsOutlined,
  ArrowBack,
  Close
} from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import StopMotionImage from 'components/play/StopMotionImage';
import PlayWorkoutIdle from 'components/play/PlayWorkoutIdle';
import PlayWorkoutSummaryModal from 'components/play/PlayWorkoutSummaryModal';
import RestAdviceNote from 'components/play/RestAdviceNote';
import { restAdviceFor } from 'components/play/restAdvice';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import AppBarContext from 'context/AppBarContext';
import { createSnack } from 'common/utils/general';
import { muscleLabel, FRAME_ASPECT } from 'components/play/constants';
import { formatDuration } from 'components/play/workoutEstimate';
import { DEFAULT_WEIGHT_KG, MET_REST, kcalFor } from 'components/play/metabolics';
import { LOG_PLAY_WORKOUT, MY_PLAY_WORKOUT_SUMMARY } from 'api/graphql/play';
import playWorkoutSlice, {
  selectWorkoutActive,
  selectWorkoutSession,
  workoutElapsedSeconds,
  restRemainingSeconds,
  currentPhaseSeconds,
  completedSetsCount
} from 'store/modules/playWorkout';
import uiSlice from 'store/modules/ui';
import { useWorkoutTick } from 'hooks/play/usePlayWorkoutTimer';
import routes from 'common/routes';
import { toJsonArg } from 'common/utils/graphqlJson';

const outlinedSx = {
  borderColor: 'hairline.strong',
  color: 'text.primary',
  fontWeight: 700,
  textTransform: 'none',
  py: 1.3,
  borderRadius: 2.5
};

// Puntos de serie: llenos = hechas, hueco dorado = la que estás haciendo, vacíos = pendientes.
function SetDots({ total, done, current = false }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }} useFlexGap>
      {Array.from({ length: total }, (_, i) => {
        const isDone = i < done;
        const isCurrent = !isDone && i === done && current;
        return (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              flex: 'none',
              border: '1px solid',
              borderColor: isDone || isCurrent ? 'gold.main' : 'hairline.strong',
              backgroundColor: isDone ? 'gold.main' : 'transparent'
            }}
          />
        );
      })}
    </Stack>
  );
}

SetDots.propTypes = {
  total: PropTypes.number.isRequired,
  done: PropTypes.number.isRequired,
  /** true = marca la serie en curso (no aplica antes de dar Play). */
  current: PropTypes.bool
};

/**
 * OTRIX Play — runner del entrenamiento ("Iniciar rutina").
 *
 * Layout de dos paneles: a la IZQUIERDA el ejercicio en grande (stop-motion, serie actual, reps y
 * cronómetro); a la DERECHA el avance de la sesión (series completadas, lista de ejercicios y cuál
 * sigue). El estado vive en Redux (persistido), así el entrenamiento sobrevive navegación y recarga;
 * esta pantalla solo lo pinta. El reloj NO arranca al entrar: se espera el Play (fase 'ready').
 */
function PlayWorkoutRunner() {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setAppBarTitle } = useContext(AppBarContext);

  const active = useSelector(selectWorkoutActive);
  const session = useSelector(selectWorkoutSession);
  const now = useWorkoutTick(active, 1000);

  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  // Resumen del cierre (verde/ámbar): mientras está abierto NO se resetea la sesión, para
  // no desmontar esta pantalla (active=false → PlayWorkoutIdle) con el modal a medias.
  const [summary, setSummary] = useState(null);
  const [logWorkout, { loading: saving }] = useMutation(LOG_PLAY_WORKOUT, {
    refetchQueries: [{ query: MY_PLAY_WORKOUT_SUMMARY }]
  });

  useEffect(() => {
    setAppBarTitle(t('workout.title', { ns: 'play' }));
  }, [setAppBarTitle, t]);

  // Vuelve por donde vino (Mi rutina / OTRIX Play / Agenda); si no hay historial, al home de Play.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(routes.otrixPlayHome.path);
  };

  // Sin entrenamiento en curso: se muestra lo que toca HOY (o recuperación si es día de
  // descanso). Así "Entrenamiento" se puede abrir desde el menú sin haber iniciado nada.
  if (!active || !session) {
    return <PlayWorkoutIdle />;
  }

  const { exercises, currentExerciseIndex, currentSet, phase } = session;
  const exercise = exercises[currentExerciseIndex] || null;
  const nextExercise = exercises[currentExerciseIndex + 1] || null;
  const totalExercises = exercises.length;
  const elapsed = workoutElapsedSeconds(session, now);
  const isDone = phase === 'done';
  const isPaused = phase === 'paused';
  const isRest = phase === 'rest';
  const isReady = phase === 'ready';

  const totalSets = exercises.reduce((acc, ex) => acc + ex.sets, 0);
  const doneSets = completedSetsCount(session);
  const progress = totalSets ? Math.min(100, (doneSets / totalSets) * 100) : 0;
  // Los ejercicios que se miden por TIEMPO no piden repeticiones: su marca es el cronómetro,
  // que ya se registra solo por serie.
  const isTimed = exercise?.benchmarkMetric === 'TIME_SECONDS';
  // Lectura en vivo del descanso frente a lo que prescribe el objetivo (ACSM).
  const restAdvice = isRest ? restAdviceFor(t, { prescribed: session.restDuration, done: currentPhaseSeconds(session, now) }) : null;

  const saveAndExit = async () => {
    // Salir sin haber hecho una sola serie no es un entrenamiento: no ensuciamos el historial
    // ni las estadísticas con una sesión vacía.
    if (doneSets === 0) {
      dispatch(playWorkoutSlice.actions.reset());
      goBack();
      return;
    }

    const at = Date.now();
    const total = workoutElapsedSeconds(session, at);
    const restRunning = session.phase === 'rest' && session.phaseStartedAt ? Math.max(0, Math.floor((at - session.phaseStartedAt) / 1000)) : 0;
    const workRunning = session.phase === 'work' && session.phaseStartedAt ? Math.max(0, Math.floor((at - session.phaseStartedAt) / 1000)) : 0;
    const restSeconds = session.restSeconds + restRunning;
    const workSeconds = session.workSeconds + workRunning;

    // Estado REAL: 100% de series = completado; menos = abandonado (con su % de éxito).
    const completionPercent = totalSets ? Math.min(100, Math.round((doneSets / totalSets) * 100)) : 0;
    const status = completionPercent >= 100 ? 'completed' : 'abandoned';

    // kcal con base MET por ejercicio (metabolics.js): el trabajo real de cada ejercicio a su
    // MET, y todo el descanso a MET de recuperación. Peso del perfil si existe (sesiones
    // viejas de redux-persist no traen `met`: fallback 5).
    const weightKg = Number(session.weightKg) > 0 ? Number(session.weightKg) : DEFAULT_WEIGHT_KG;
    let workAccounted = 0;
    let calories = 0;
    exercises.forEach((ex) => {
      const measured = (ex.secondsDone || [])
        .slice(0, ex.setsDone)
        .filter((v) => Number.isFinite(Number(v)) && Number(v) > 0)
        .reduce((acc, v) => acc + Number(v), 0);
      const exWork = measured > 0 ? measured : (ex.setsDone || 0) * (ex.secondsPerSet || 30);
      workAccounted += exWork;
      calories += kcalFor(exWork, ex.met || 5, weightKg);
    });
    // Trabajo no atribuible a un ejercicio (transiciones): MET de recuperación.
    calories += kcalFor(Math.max(0, workSeconds - workAccounted) + restSeconds, MET_REST, weightKg);
    const estimatedCalories = Math.max(1, Math.round(calories));

    try {
      await logWorkout({
        variables: {
          input: {
            day_of_week: session.dayOfWeek || null,
            muscle_focus: session.muscleFocus || null,
            started_at: new Date(session.startedAt).toISOString(),
            total_seconds: total,
            work_seconds: workSeconds,
            rest_seconds: restSeconds,
            exercise_count: totalExercises,
            total_sets: doneSets,
            estimated_calories: estimatedCalories,
            completion_percent: completionPercent,
            status,
            // El escalar `JSON` del backend hace json_decode al recibir: hay que mandarlo
            // SERIALIZADO o la mutación revienta antes del resolver (ver common/utils/graphqlJson).
            details: toJsonArg(exercises.map((ex) => ({
              exercise_id: ex.exerciseId,
              name: ex.name,
              metric: ex.benchmarkMetric,
              sets: ex.sets,
              sets_done: ex.setsDone,
              // `reps` es el OBJETIVO (referencia); `reps_done`/`seconds_done` son lo que
              // realmente pasó y es de donde el backend saca la marca.
              reps: ex.repsLabel || String(ex.reps),
              reps_done: ex.repsDone,
              seconds_done: ex.secondsDone,
              weights: ex.weights,
              // Descanso prescrito vs. real: deja el aviso ACSM reconstruible desde el historial.
              rest_prescribed: ex.restBetweenSets,
              rest_done: ex.restDone || [],
              rest_skipped: ex.restSkipped || []
            })))
          }
        }
      });
      // El cierre lo cuenta el modal de resumen (verde/ámbar + CTA nutrición); la sesión
      // se resetea al salir del modal para no desmontar esta pantalla antes de tiempo.
      setSummary({
        completed: status === 'completed',
        percent: completionPercent,
        totalSeconds: total,
        doneSets,
        totalSets,
        calories: estimatedCalories,
        // Copia PLANA del plan: al cerrar el modal se resetea la sesión, y el desglose por
        // ejercicio no puede depender de un estado que ya se limpió.
        exercises: exercises.map((ex) => ({
          id: ex.itemId || ex.exerciseId,
          name: ex.name,
          metric: ex.benchmarkMetric,
          sets: ex.sets,
          setsDone: ex.setsDone || 0,
          reps: ex.repsLabel || String(ex.reps),
          repsDone: [...(ex.repsDone || [])],
          secondsDone: [...(ex.secondsDone || [])],
          weights: [...(ex.weights || [])],
          restBetweenSets: ex.restBetweenSets,
          restDone: [...(ex.restDone || [])],
          restSkipped: [...(ex.restSkipped || [])]
        }))
      });
    } catch (e) {
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ variant: 'error', message: t('workout.saveError', { ns: 'play' }) })));
      dispatch(playWorkoutSlice.actions.reset());
      navigate(routes.clientDashboard.path);
    }
  };

  const handleCompleteSet = () => {
    // Si no tocó el campo, se asume que cumplió el objetivo (el placeholder ya lo anuncia).
    const repsDone = isTimed ? null : reps || exercise?.reps;
    dispatch(playWorkoutSlice.actions.completeSet({ reps: repsDone, weight, now: Date.now() }));
    setWeight('');
    setReps('');
  };

  return (
    <Stack spacing={2.5}>
      {/* Cabecera: atrás + tiempo total + progreso + salir */}
      <PremiumCard padding={{ xs: 2, md: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={goBack}
              aria-label={t('workout.back', { ns: 'play' })}
              sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' }, '&:active': { transform: 'scale(0.98)' } }}>
              <ArrowBack />
            </IconButton>
            <Stack spacing={0.25}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                {t('workout.elapsed', { ns: 'play' })}
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatDuration(elapsed)}
              </Typography>
            </Stack>
          </Stack>
          <Stack alignItems="flex-end" spacing={0.75}>
            {session.muscleFocus && (
              <Chip size="small" label={muscleLabel(t, session.muscleFocus)} sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700 }} />
            )}
            {/* Semana del mesociclo con la que se armó este plan (monocromo: el dorado ya lo lleva el foco). */}
            {session.progressionWeek && (
              <Chip
                size="small"
                label={session.progressionWeek === 4 ? t('mesocycle.deload', { ns: 'play' }) : t('mesocycle.week', { ns: 'play', week: session.progressionWeek })}
                sx={{ bgcolor: 'surface.raised', color: 'text.secondary', fontWeight: 700 }}
              />
            )}
            <Button size="small" startIcon={<Close />} onClick={saveAndExit} disabled={saving} sx={{ color: 'text.secondary', textTransform: 'none' }}>
              {t('workout.finish', { ns: 'play' })}
            </Button>
          </Stack>
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'surface.overlay',
              '& .MuiLinearProgress-bar': { backgroundColor: 'gold.main', borderRadius: 3 }
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
            {t('workout.exerciseOf', { ns: 'play', current: Math.min(currentExerciseIndex + 1, totalExercises), total: totalExercises })}
          </Typography>
        </Box>
      </PremiumCard>

      {isDone ? (
        <PremiumCard>
          <Stack spacing={2.5} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{ color: 'gold.main' }}>
              <EmojiEventsOutlined sx={{ fontSize: 56 }} />
            </Box>
            <Stack spacing={0.5}>
              <Typography variant="h5" fontWeight={800}>
                {t('workout.done', { ns: 'play' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('workout.doneBody', { ns: 'play' })}
              </Typography>
            </Stack>
            <Typography variant="h3" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatDuration(elapsed)}
            </Typography>
            <Button fullWidth sx={{ ...premiumButtonSx, maxWidth: 320 }} onClick={saveAndExit} disabled={saving}>
              {t('workout.save', { ns: 'play' })}
            </Button>
          </Stack>
        </PremiumCard>
      ) : (
        <Grid container spacing={2.5} alignItems="stretch">
          {/* IZQUIERDA — el ejercicio en grande */}
          <Grid item xs={12} md={7}>
            <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                {/* Stop-motion protagonista. La caja sigue la proporción del fotograma (9:16) y se
                    centra: si se dejara a lo ancho del panel, la foto vertical quedaría flotando
                    entre dos barrotes enormes. */}
                {(exercise?.image1 || exercise?.image2) && (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 280 * FRAME_ASPECT,
                      mx: 'auto',
                      borderRadius: '14px',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'hairline.main',
                      backgroundColor: 'surface.overlay'
                    }}>
                    <StopMotionImage frame1={exercise.image1} frame2={exercise.image2} alt={exercise.name} height={280} />
                  </Box>
                )}

                <Stack spacing={0.75}>
                  <Typography variant="h4" fontWeight={800} letterSpacing="-0.01em">
                    {exercise?.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {exercise?.muscle && (
                      <Chip size="small" label={muscleLabel(t, exercise.muscle)} sx={{ bgcolor: 'surface.overlay' }} />
                    )}
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                      {t('workout.setOf', { ns: 'play', current: currentSet, total: exercise?.sets || 1 })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      · {t('workout.reps', { ns: 'play', count: exercise?.reps || 0 })}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      · {t('workout.perSet', { ns: 'play', seconds: exercise?.secondsPerSet || 0 })}
                    </Typography>
                  </Stack>
                  <SetDots total={exercise?.sets || 1} done={exercise?.setsDone || 0} current={!isReady} />
                </Stack>

                {/* Fase: listo / descanso / trabajo */}
                {(() => {
                  if (isReady) {
                    return (
                      <Stack spacing={2} alignItems="center" sx={{ py: 2, mt: 'auto' }}>
                        <Stack spacing={0.5} alignItems="center">
                          <Typography variant="h6" fontWeight={800}>
                            {t('workout.readyTitle', { ns: 'play' })}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            {t('workout.readyBody', { ns: 'play' })}
                          </Typography>
                        </Stack>
                        <Button
                          fullWidth
                          startIcon={<PlayArrow />}
                          onClick={() => dispatch(playWorkoutSlice.actions.begin({ now: Date.now() }))}
                          sx={{ ...premiumButtonSx, maxWidth: 320 }}>
                          {t('workout.start', { ns: 'play' })}
                        </Button>
                      </Stack>
                    );
                  }

                  if (isRest) {
                    return (
                      <Stack spacing={2} alignItems="center" sx={{ py: 1, mt: 'auto' }}>
                        <Typography variant="overline" sx={{ color: 'gold.main', letterSpacing: '0.16em', fontWeight: 700 }}>
                          {t('workout.rest', { ns: 'play' })}
                        </Typography>
                        <Typography variant="h1" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', fontSize: { xs: '3.5rem', md: '4.5rem' }, lineHeight: 1, color: 'text.primary' }}>
                          {formatDuration(restRemainingSeconds(session, now))}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {currentSet >= (exercise?.sets || 1) ? t('workout.restNextExercise', { ns: 'play' }) : t('workout.restNext', { ns: 'play' })}
                        </Typography>
                        {/* Aviso ACSM: informa lo que cuesta recortar el descanso, nunca impide saltarlo. */}
                        {restAdvice && <RestAdviceNote advice={restAdvice} sx={{ maxWidth: 360 }} />}
                        <Button
                          startIcon={<SkipNext />}
                          onClick={() => dispatch(playWorkoutSlice.actions.endRest({ now: Date.now(), skipped: true }))}
                          sx={{ ...premiumButtonSx, maxWidth: 320, width: '100%' }}>
                          {t('workout.skipRest', { ns: 'play' })}
                        </Button>
                      </Stack>
                    );
                  }

                  return (
                    <Stack spacing={2} alignItems="center" sx={{ py: 1, mt: 'auto' }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: isPaused ? 'text.secondary' : 'text.primary' }}>
                        <DirectionsRun sx={{ color: 'gold.main' }} />
                        <Typography variant="overline" sx={{ letterSpacing: '0.16em', fontWeight: 700 }}>
                          {isPaused ? t('workout.paused', { ns: 'play' }) : t('workout.work', { ns: 'play' })}
                        </Typography>
                      </Stack>
                      <Typography variant="h1" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', fontSize: { xs: '3.5rem', md: '4.5rem' }, lineHeight: 1 }}>
                        {formatDuration(currentPhaseSeconds(session, now))}
                      </Typography>
                      {/* Reps REALES, prellenadas con el objetivo: es un toque si las cumpliste
                          y una corrección si no. Antes solo se pedía el peso y se registraba el
                          objetivo como si lo hubieras hecho. */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%', maxWidth: 320 }}>
                        {!isTimed && (
                          <TextField
                            type="number"
                            size="small"
                            value={reps}
                            onChange={(e) => setReps(e.target.value)}
                            placeholder={String(exercise?.reps ?? '')}
                            label={t('workout.repsDone', { ns: 'play' })}
                            inputProps={{ min: 0, inputMode: 'numeric' }}
                            sx={{ flex: 1 }}
                          />
                        )}
                        <TextField
                          type="number"
                          size="small"
                          value={weight}
                          onChange={(e) => setWeight(e.target.value)}
                          label={`${t('workout.weight', { ns: 'play' })} · ${t('workout.weightOptional', { ns: 'play' })}`}
                          inputProps={{ min: 0, inputMode: 'decimal' }}
                          sx={{ flex: 1 }}
                        />
                      </Stack>
                      <Button
                        fullWidth
                        startIcon={<CheckCircle />}
                        onClick={handleCompleteSet}
                        disabled={isPaused}
                        sx={{ ...premiumButtonSx, maxWidth: 320 }}>
                        {t('workout.completeSet', { ns: 'play' })}
                      </Button>
                      <Stack direction="row" spacing={1.5} justifyContent="center">
                        {isPaused ? (
                          <Button variant="outlined" startIcon={<PlayArrow />} onClick={() => dispatch(playWorkoutSlice.actions.resume({ now: Date.now() }))} sx={outlinedSx}>
                            {t('workout.resume', { ns: 'play' })}
                          </Button>
                        ) : (
                          <Button variant="outlined" startIcon={<Pause />} onClick={() => dispatch(playWorkoutSlice.actions.pause({ now: Date.now() }))} sx={outlinedSx}>
                            {t('workout.pause', { ns: 'play' })}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  );
                })()}
              </Stack>
            </PremiumCard>
          </Grid>

          {/* DERECHA — avance de la sesión */}
          <Grid item xs={12} md={5}>
            <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
              <Stack spacing={2.5} sx={{ height: '100%' }}>
                <Stack spacing={0.25}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {t('workout.setsDone', { ns: 'play' })}
                  </Typography>
                  <Typography variant="h3" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                    {doneSets}
                    <Typography component="span" variant="h5" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                      /{totalSets}
                    </Typography>
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {t('workout.exercises', { ns: 'play' })}
                  </Typography>
                  {exercises.map((ex, idx) => {
                    const isCurrent = idx === currentExerciseIndex;
                    const finished = ex.setsDone >= ex.sets;
                    return (
                      <Stack
                        key={ex.itemId || ex.exerciseId}
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{
                          p: 1.25,
                          borderRadius: '12px',
                          border: '1px solid',
                          borderColor: isCurrent ? 'hairline.strong' : 'hairline.main',
                          backgroundColor: isCurrent ? 'selected.bg' : 'surface.raised',
                          opacity: !isCurrent && finished ? 0.6 : 1
                        }}>
                        <Box sx={{ color: finished ? 'gold.main' : 'text.secondary', display: 'flex', flex: 'none' }}>
                          {finished ? <CheckCircle sx={{ fontSize: 18 }} /> : <CheckCircleOutline sx={{ fontSize: 18 }} />}
                        </Box>
                        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={800} noWrap sx={{ color: isCurrent ? 'selected.fg' : 'text.primary' }}>
                            {ex.name}
                          </Typography>
                          <SetDots total={ex.sets} done={ex.setsDone} current={isCurrent && !isReady} />
                        </Stack>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
                          {ex.setsDone}/{ex.sets}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>

                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                    {t('workout.nextExercise', { ns: 'play' })}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: nextExercise ? 'text.primary' : 'text.secondary' }} noWrap>
                    {nextExercise ? nextExercise.name : t('workout.noNext', { ns: 'play' })}
                  </Typography>
                </Box>
              </Stack>
            </PremiumCard>
          </Grid>
        </Grid>
      )}

      <PlayWorkoutSummaryModal
        open={Boolean(summary)}
        summary={summary}
        onViewPerformance={() => {
          setSummary(null);
          dispatch(playWorkoutSlice.actions.reset());
          navigate(routes.playPerformance.path);
        }}
        onViewNutrition={() => {
          setSummary(null);
          dispatch(playWorkoutSlice.actions.reset());
          navigate(routes.clientProgram.path);
        }}
      />
    </Stack>
  );
}

export default PlayWorkoutRunner;
